import * as React from "react";
import { FILTER_SEPARATOR } from "@/constants";
import { Alert, Box, Button, Card, CardContent, LinearProgress, Stack, TextField, Typography } from "@mui/material";
import { CheckCircleIcon, CircleIcon, UsersThreeIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import {
	useLeadPurchase,
	type LeadPurchase,
	type LeadPurchaseResult,
	type LeadSearch,
	type LeadSearchPurchase,
} from "@/hooks/api/use-lead-finder";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";

import { LeadFieldsPrompt } from "./lead-fields-prompt";

/** The most one purchase takes; the API enforces the same. */
const MAX_LEADS = 1000;

const STEPS: { key: NonNullable<LeadPurchase["progress"]>["step"]; label: string }[] = [
	{ key: "finding", label: "Finding contacts you may call" },
	{ key: "looking-up", label: "Getting their numbers from ZoomInfo" },
	{ key: "saving", label: "Saving them to your group" },
];

export interface PurchaseCardProps {
	pluginId: string;
	search: LeadSearch;
	total: number;
	/** A search is running; buying waits for it. */
	busy: boolean;
	/** The purchase is being started. */
	starting: boolean;
	/** The purchase under way or done; the page holds it so editing the search cannot lose it. */
	purchaseId: string | null;
	/** What was already bought from this search. */
	purchases: LeadSearchPurchase[];
	onBuy: (input: { count: number; groupName: string }) => Promise<void>;
	/** Back to the form, from a purchase that ended. */
	onClose: () => void;
}

/** Why a finished purchase holds fewer leads than were asked for. */
function shortfall(result: LeadPurchaseResult): string | null {
	const lookedUp = result.lookedUp.toLocaleString();
	if (result.stoppedEarly === "credit_limit")
		return result.lookedUp
			? `Your ZoomInfo credit limit was reached after ${lookedUp} lookups, so you got fewer than ${result.wanted.toLocaleString()}.`
			: "Your ZoomInfo credit limit was reached before any lead could be looked up.";
	if (result.stoppedEarly === "pages")
		return `This search's top matches held only ${lookedUp} new contacts you may call, so you got fewer than ${result.wanted.toLocaleString()}. Many are already your contacts or cannot be called; narrowing the search reaches others.`;
	if (result.stoppedEarly === "error")
		return `ZoomInfo stopped answering after ${lookedUp} lookups. You keep what was bought; search again later for more.`;
	if (result.lookedUp < result.wanted)
		return `ZoomInfo had ${lookedUp} ${result.inAccount ? "new " : ""}contacts you may call for this search, fewer than the ${result.wanted.toLocaleString()} asked for.`;
	return null;
}

export function PurchaseCard({
	pluginId,
	search,
	total,
	busy,
	starting,
	purchaseId,
	purchases,
	onBuy,
	onClose,
}: PurchaseCardProps): React.JSX.Element {
	const navigate = useNavigate();
	const available = Math.min(total, MAX_LEADS);
	const [count, setCount] = React.useState(String(Math.min(search.count, available) || ""));
	const [groupName, setGroupName] = React.useState(search.groupName);
	const [confirming, setConfirming] = React.useState(false);
	const { data: purchase, isError } = useLeadPurchase(pluginId, purchaseId);

	const amount = Number(count);
	const valid = Number.isInteger(amount) && amount >= 1 && amount <= available && groupName.trim().length > 0;

	if (purchaseId) {
		const result = purchase?.result;
		const lost = !result && purchase?.state !== "failed" && isError;
		if (purchase?.state === "failed" || lost)
			return (
				<Card sx={{ borderRadius: "16px" }}>
					<CardContent>
						<Stack spacing={2}>
							{lost ? (
								<Alert severity="warning">
									We could not check on this purchase. Any leads it gets still go into “{groupName.trim()}”; look for
									that group under Contacts.
								</Alert>
							) : (
								<Alert severity="error">{purchase?.error ?? "The purchase stopped."}</Alert>
							)}
							<Button variant="outlined" onClick={onClose}>
								Back
							</Button>
						</Stack>
					</CardContent>
				</Card>
			);
		if (result) {
			const inGroup = result.added + result.alreadyHad;
			const why = shortfall(result);
			const lines = [
				`${result.added.toLocaleString()} new contacts`,
				result.alreadyHad > 0 && `${result.alreadyHad.toLocaleString()} you already had`,
				result.inAccount > 0 &&
					`${result.inAccount.toLocaleString()} already in your contacts, left out without using credits`,
				result.doNotCall > 0 && `${result.doNotCall.toLocaleString()} Do Not Call numbers left out`,
				result.skipped > 0 && `${result.skipped.toLocaleString()} without a number we may call`,
				result.failed > 0 && `${result.failed.toLocaleString()} could not be saved`,
			].filter(Boolean);
			return (
				<Card sx={{ borderRadius: "16px" }}>
					<CardContent>
						<Stack spacing={2}>
							<Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
								{result.groupId ? (
									<CheckCircleIcon size={28} weight="fill" color="var(--mui-palette-success-main)" />
								) : (
									<WarningCircleIcon size={28} weight="fill" color="var(--mui-palette-warning-main)" />
								)}
								<Typography variant="h6">
									{result.groupId
										? `${inGroup.toLocaleString()} leads in “${result.groupName}”`
										: "No leads were added"}
								</Typography>
							</Stack>
							{result.groupId ? (
								<Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
									{lines.map((line) => (
										<Typography component="li" key={String(line)} variant="body2" color="text.secondary">
											{line}
										</Typography>
									))}
								</Stack>
							) : (
								result.lookedUp > 0 && (
									<Typography variant="body2" color="text.secondary">
										None of the {result.lookedUp.toLocaleString()} contacts ZoomInfo looked up had a number we may call.
									</Typography>
								)
							)}
							{why && <Alert severity="warning">{why}</Alert>}
							{result.notSuppressed > 0 && (
								<Alert severity="warning">
									{result.notSuppressed.toLocaleString()} Do Not Call numbers could not be added to your suppression
									list. None of these leads uses one, but check for them elsewhere in your contacts.
								</Alert>
							)}
							{result.groupId && <LeadFieldsPrompt pluginId={pluginId} />}
							{result.groupId && (
								<Button
									variant="contained"
									startIcon={<UsersThreeIcon />}
									onClick={() =>
										navigate(
											`/contacts?groupId=${encodeURIComponent(`${result.groupName}${FILTER_SEPARATOR}${result.groupId}`)}`
										)
									}
								>
									View contacts
								</Button>
							)}
							<Button variant="text" onClick={onClose}>
								Find more leads
							</Button>
						</Stack>
					</CardContent>
				</Card>
			);
		}
		const progress = purchase?.progress;
		const current = progress ? STEPS.findIndex((step) => step.key === progress.step) : -1;
		return (
			<Card sx={{ borderRadius: "16px" }}>
				<CardContent>
					<Stack spacing={2}>
						<Typography variant="h6">Getting your leads</Typography>
						<Typography variant="body2" color="text.secondary">
							You can leave this page; the leads keep coming into “{groupName.trim()}”.
						</Typography>
						<Stack spacing={1.25}>
							{STEPS.map((step, index) => (
								<Stack key={step.key} direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
									{index < current ? (
										<CheckCircleIcon weight="fill" color="var(--mui-palette-success-main)" />
									) : (
										<CircleIcon
											color={index === current ? "var(--mui-palette-primary-main)" : "var(--mui-palette-text-disabled)"}
										/>
									)}
									<Typography
										variant="body2"
										color={index <= current ? "text.primary" : "text.secondary"}
										aria-live={index === current ? "polite" : undefined}
									>
										{step.label}
										{index === current && progress && progress.total > 0
											? ` (${progress.done} of ${progress.total})`
											: ""}
									</Typography>
								</Stack>
							))}
						</Stack>
						<LinearProgress
							aria-label="Purchase progress"
							variant={progress && progress.total > 0 ? "determinate" : "indeterminate"}
							value={progress && progress.total > 0 ? (progress.done / progress.total) * 100 : undefined}
						/>
					</Stack>
				</CardContent>
			</Card>
		);
	}

	const already = purchases.reduce((sum, { count: bought }) => sum + bought, 0);

	// The form is a bar pinned to the bottom of the results: what you get, where it goes and what it costs stay in
	// view while you look through who fits.
	return (
		<Card
			sx={{
				// Pinned on wider screens only: on a phone the wrapped bar would cover most of the results.
				position: { md: "sticky" },
				bottom: 16,
				zIndex: 2,
				borderRadius: "16px",
				boxShadow: "var(--mui-shadows-16)",
				px: 3,
				py: 2,
			}}
		>
			<Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
				<Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
					<Typography variant="subtitle2">Get leads into “{groupName.trim() || "a new group"}”</Typography>
					<Typography variant="body2" color="text.secondary">
						{valid
							? `Uses up to ${amount.toLocaleString()} ZoomInfo credit${amount === 1 ? "" : "s"}`
							: "Uses one ZoomInfo credit a lead"}
						, none for people your ZoomInfo account enriched in the last 12 months. Do Not Call numbers and your own
						contacts are left out.
						{already > 0 && ` You already got ${already.toLocaleString()} leads from this search.`}
					</Typography>
				</Box>
				<TextField
					label="Group name"
					size="small"
					value={groupName}
					onChange={(event) => setGroupName(event.target.value)}
					inputProps={{ maxLength: 80 }}
					disabled={busy || starting}
					sx={{ width: 220 }}
				/>
				<TextField
					label="How many"
					type="number"
					size="small"
					value={count}
					onChange={(event) => setCount(event.target.value)}
					inputProps={{ min: 1, max: available, step: 1, title: `Up to ${available.toLocaleString()}` }}
					error={count !== "" && !valid && groupName.trim().length > 0}
					helperText={`Up to ${available.toLocaleString()}`}
					disabled={busy || starting}
					sx={{ width: 110 }}
				/>
				<Button
					size="large"
					variant="contained"
					disabled={busy || !valid || starting}
					onClick={() => setConfirming(true)}
					sx={{ height: 44, borderRadius: "10px", flexShrink: 0 }}
				>
					{valid ? `Get ${amount.toLocaleString()} leads` : "Get leads"}
				</Button>
			</Stack>
			<ConfirmModal
				open={confirming}
				// Once confirmed the purchase is on its way: closing then would hide it, not stop it.
				onClose={() => !starting && setConfirming(false)}
				onSumbit={() => onBuy({ count: amount, groupName: groupName.trim() })}
				title={`Get ${valid ? amount.toLocaleString() : ""} leads from ZoomInfo?`}
				description={`This can use up to ${valid ? amount.toLocaleString() : ""} of your ZoomInfo credits. The leads go into a new group, "${groupName.trim()}".`}
			/>
		</Card>
	);
}
